// A "last N prompts" page (CYBORG-1180) sends a run of rows as ONE item listing the other
// seqs it stands for in `coalescedSeqs`. Expand it back to one row per seq so the store's
// seq dedupe, applied set and chunk joins see exactly the rows an ordinary page carries.
// A text row keeps its whole text on its first seq and "" on the rest; any other row
// (a tool call's latest state) repeats on every seq, which the store applies in place.

const TEXT_TYPES = new Set(["assistant_message", "reasoning"]);

function coalescedSeqsOf(item: Record<string, unknown>): number[] | null {
  const seqs = item.coalescedSeqs;
  if (!Array.isArray(seqs) || seqs.length === 0) return null;
  return seqs.filter((s): s is number => typeof s === "number");
}

export function expandCoalescedRows(items: Record<string, unknown>[]): Record<string, unknown>[] {
  if (!items.some((item) => coalescedSeqsOf(item) !== null)) return items;
  const out: Record<string, unknown>[] = [];
  for (const item of items) {
    const seqs = coalescedSeqsOf(item);
    if (seqs === null) {
      out.push(item);
      continue;
    }
    const base: Record<string, unknown> = { ...item };
    delete base.coalescedSeqs;
    const isText = TEXT_TYPES.has(String(base.type));
    if (isText) base.coalescedBase = base.seq;
    out.push(base);
    for (const seq of seqs) out.push(isText ? { ...base, text: "", seq } : { ...base, seq });
  }
  const seqOf = (row: Record<string, unknown>) =>
    typeof row.seq === "number" ? row.seq : Number.POSITIVE_INFINITY;
  return out.toSorted((a, b) => seqOf(a) - seqOf(b));
}

// A merged text group lands whole or not at all: over a seq the screen holds, its text would be
// lost silently. Its rows are left out and its unheld seqs come back as holes to read back.
export function unmergedOverHeld<R extends Record<string, unknown> & { seq: number }>(
  rows: readonly R[],
  held: ReadonlySet<number>,
): { rows: R[]; holes: Array<{ after: number; before: number }> } {
  const groups = new Map<number, number[]>();
  for (const row of rows) {
    const group = row.coalescedBase;
    if (typeof group === "number") groups.set(group, [...(groups.get(group) ?? []), row.seq]);
  }
  const dropped = new Set<number>();
  const holes: Array<{ after: number; before: number }> = [];
  for (const [group, seqs] of groups) {
    if (!seqs.some((seq) => held.has(seq))) continue;
    dropped.add(group);
    const open = seqs.filter((seq) => !held.has(seq)).toSorted((a, b) => a - b);
    for (let i = 0; i < open.length; i += 1) {
      let j = i;
      while (j + 1 < open.length && open[j + 1] === open[j] + 1) j += 1;
      holes.push({ after: open[i] - 1, before: open[j] + 1 });
      i = j;
    }
  }
  if (dropped.size === 0) return { rows: [...rows], holes };
  const kept = rows.filter((r) => !(typeof r.coalescedBase === "number" && dropped.has(r.coalescedBase))); // prettier-ignore
  return { rows: kept, holes };
}
